import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import type { UserPresence } from '../hooks/usePokeverseCursors';

interface PresenceCursorsProps {
  cursors: UserPresence[];
  containerRef: React.RefObject<HTMLDivElement>;
  currentViewLevel: 'series' | 'sets' | 'cards';
  currentViewMode: 'galaxy' | 'timeline';
}

// Custom cursor SVG - memoized for performance
const CursorIcon = memo(({ color }: { color: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="drop-shadow-lg"
  >
    <path
      d="M5.5 3.5L20.5 12L12 14L9 21.5L5.5 3.5Z"
      fill={color}
      stroke="white"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
));

CursorIcon.displayName = 'CursorIcon';

// Individual cursor with smooth spring animation
const SmoothCursor = memo(({ 
  presence, 
  containerRect 
}: { 
  presence: UserPresence; 
  containerRect: DOMRect;
}) => {
  // Calculate target position
  const targetX = containerRect.left + (presence.cursor.x * containerRect.width);
  const targetY = containerRect.top + (presence.cursor.y * containerRect.height);

  // Use spring for ultra-smooth interpolation
  const springConfig = { stiffness: 800, damping: 35, mass: 0.1 };
  const x = useSpring(targetX, springConfig);
  const y = useSpring(targetY, springConfig);

  // Update spring targets when position changes
  useEffect(() => {
    x.set(targetX);
    y.set(targetY);
  }, [targetX, targetY, x, y]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5 }}
      transition={{ duration: 0.15 }}
      className="absolute top-0 left-0"
      style={{ 
        x, 
        y,
        willChange: 'transform',
      }}
    >
      {/* Cursor pointer */}
      <CursorIcon color={presence.color} />
      
      {/* Nickname badge */}
      <div 
        className="absolute left-5 top-5 px-2 py-0.5 rounded-full text-xs font-medium text-white shadow-lg whitespace-nowrap"
        style={{ 
          backgroundColor: presence.color,
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {presence.odersNickname}
      </div>
    </motion.div>
  );
});

SmoothCursor.displayName = 'SmoothCursor';

export function PresenceCursors({ cursors, containerRef, currentViewLevel, currentViewMode }: PresenceCursorsProps) {
  const [containerRect, setContainerRect] = useState<DOMRect | null>(null);
  const rafRef = useRef<number>();

  // Update container rect on resize with RAF for performance
  useEffect(() => {
    const updateRect = () => {
      if (containerRef.current) {
        setContainerRect(containerRef.current.getBoundingClientRect());
      }
    };

    const handleResize = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateRect);
    };

    updateRect();
    window.addEventListener('resize', handleResize);
    
    // Also update on scroll
    window.addEventListener('scroll', handleResize, { passive: true });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef]);

  if (!containerRect) return null;

  // Filter cursors to only show those on the same view level AND view mode
  const visibleCursors = cursors.filter(cursor => 
    cursor.cursor.viewLevel === currentViewLevel && 
    cursor.cursor.viewMode === currentViewMode
  );

  return (
    <div className="fixed inset-0 pointer-events-none z-[100]" aria-hidden="true">
      <AnimatePresence mode="popLayout">
        {visibleCursors.map((presence) => (
          <SmoothCursor 
            key={presence.odersId} 
            presence={presence} 
            containerRect={containerRect} 
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

// Active users indicator component
interface ActiveUsersIndicatorProps {
  count: number;
  isConnected: boolean;
}

export function ActiveUsersIndicator({ count, isConnected }: ActiveUsersIndicatorProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20"
    >
      {/* Connection status dot */}
      <div className="relative flex items-center">
        <div 
          className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`}
        />
        {isConnected && (
          <div className="absolute w-2 h-2 rounded-full bg-green-500 animate-ping opacity-75" />
        )}
      </div>
      
      {/* User count */}
      <span className="text-sm text-white/80">
        <span className="font-semibold">{count}</span>
        <span className="text-white/60 ml-1">
          {count === 1 ? 'collector' : 'collectors'}
        </span>
      </span>
    </motion.div>
  );
}

